// src/App.js
import React, { useEffect } from 'react';
import './App.css';
import { auth } from './firebase';
import { BrowserRouter as Router, Routes, Route, Link as RouterLink, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// --- React Bootstrap Imports ---
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import NavDropdown from 'react-bootstrap/NavDropdown';

// --- Component Imports ---
// ... (your component imports remain the same) ...
import FamilyList from './components/FamilyList';
import FamilyDetail from './components/FamilyDetail';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import GroupsPage from './components/GroupsPage';
import AddMemberPage from './components/AddMemberPage';
import EditMemberPage from './components/EditMemberPage';
import Home from './components/Home';
import Footer from './components/Footer';
import UserManagement from './components/UserManagement';
import ForgotPassword from './components/ForgotPassword';
import AddFamilyPage from './components/AddFamilyPage';
import EditFamilyPage from './components/EditFamilyPage';
import UserProfilePage from './components/UserProfilePage';
import CreateAccountPage from './components/CreateAccountPage';
import RegisterOrganizationPage from './components/RegisterOrganization';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import ProtectedRoute from './components/ProtectedRoute';
import AddGroupPage from './components/AddGroupPage';
import MapPage from './components/MapPage';
import DataAuditPage from './components/DataAuditPage';
import OrgManagementPage from './components/OrgManagementPage';
import AssignMemberPhotos from './components/AssignMemberPhotos';
import AssignSingleMemberPhotoPage from './components/AssignSingleMemberPhotoPage';
import MemberDetail from './components/MemberDetail';
import EventsPage from './components/EventsPage';
import AddEventPage from './components/AddEventPage';
import EditEventPage from './components/EditEventPage';
import EventDetailPage from './components/EventDetailPage';
import AttendancePage from './components/AttendancePage';
import AttendanceTypesPage from './components/AttendanceTypesPage';
import OrganizationSettingsPage from './components/OrganizationSettingsPage';
import ActivityLogPage from './components/ActivityLogPage';
import CommunicationsPage from './components/CommunicationsPage';
import JoinPage from './components/JoinPage';
import JoinRequestsPage from './components/JoinRequestsPage';
import AnnouncementForm from './components/AnnouncementForm';
import AutomationForm from './components/AutomationForm';
import FeaturesPage from './components/FeaturesPage';
import GuidesIndex from './components/GuidesIndex';
import GuidePage from './components/GuidePage';
import ImportDirectoryPage from './components/ImportDirectoryPage';
import NewsletterSignupPage from './components/NewsletterSignupPage';
import AnalyticsDashboardPage from './components/AnalyticsDashboardPage';
import ErrorBoundary from './components/ErrorBoundary';
import MemberPhotoAvatar from './components/MemberPhotoAvatar';

// Font Awesome Icons - CORRECTED IMPORTS
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faQuestionCircle,
    faAddressCard // Regular or Solid
} from '@fortawesome/free-regular-svg-icons'; // Icons that are often preferred in their 'regular' style

import {
    faUserCircle,
    faSignOutAlt,
    faTachometerAlt,
    faUsers,
    faMapMarkedAlt,
    faCog,
    faClipboardList,
    faBuilding,
    faCalendarAlt,
    faChartLine,
    faHistory,
    faEnvelope,
    faFileImport
} from '@fortawesome/free-solid-svg-icons';


const SUPER_ADMIN_EMAIL = "jonstewart24@gmail.com";

// Inner component that has access to the Auth Context
function AppContent() {
  const { currentUser, loadingAuth, isAdmin, memberPhoto } = useAuth();
  const location = useLocation();
  const isSuperAdmin = currentUser?.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();
  const isMarketingPage = ['/features', '/register-organization', '/guides', '/privacy'].some(p => location.pathname.startsWith(p));

  // Track page views on route change
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search + location.hash,
      });
    }
  }, [location]);

  // Helper to check if path is active
  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  if (loadingAuth) {
    return ( <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}> <div>Loading Application...</div> </div> );
  }

  return (
      <div className="App d-flex flex-column min-vh-100">
        <header>
            <Navbar variant="dark" expand="lg" collapseOnSelect sticky="top" className="main-navbar">
                <Container fluid>
                    <Navbar.Brand as={RouterLink} to={currentUser ? "/dashboard" : "/"}>
                        <img src="/assets/logoCA24.png" width="182" height="30" alt="Logo" className="d-inline-block align-top"/>
                    </Navbar.Brand>
                    <Navbar.Toggle aria-controls="responsive-navbar-nav" />
                    <Navbar.Collapse id="responsive-navbar-nav">
                        {/* Left Nav Items */}
                        <Nav className="me-auto">
                            {currentUser && (
                                <>
                                    <Nav.Link
                                      as={RouterLink}
                                      to="/dashboard"
                                      className={`nav-item-custom ${isActive('/dashboard') ? 'active' : ''}`}
                                    >
                                        <FontAwesomeIcon icon={faTachometerAlt} className="nav-icon" />
                                        <span>Dashboard</span>
                                    </Nav.Link>
                                    <Nav.Link
                                      as={RouterLink}
                                      to="/families"
                                      className={`nav-item-custom ${isActive('/families') ? 'active' : ''}`}
                                    >
                                        <FontAwesomeIcon icon={faAddressCard} className="nav-icon" />
                                        <span>Families</span>
                                    </Nav.Link>
                                    <Nav.Link
                                      as={RouterLink}
                                      to="/groups"
                                      className={`nav-item-custom ${isActive('/groups') ? 'active' : ''}`}
                                    >
                                        <FontAwesomeIcon icon={faUsers} className="nav-icon" />
                                        <span>Groups</span>
                                    </Nav.Link>
                                    <Nav.Link
                                      as={RouterLink}
                                      to="/events"
                                      className={`nav-item-custom ${isActive('/events') ? 'active' : ''}`}
                                    >
                                        <FontAwesomeIcon icon={faCalendarAlt} className="nav-icon" />
                                        <span>Events</span>
                                    </Nav.Link>
                                    <Nav.Link
                                      as={RouterLink}
                                      to="/map"
                                      className={`nav-item-custom ${isActive('/map') ? 'active' : ''}`}
                                    >
                                        <FontAwesomeIcon icon={faMapMarkedAlt} className="nav-icon" />
                                        <span>Map</span>
                                    </Nav.Link>
                                    {isAdmin && (
                                        <>
                                            <Nav.Link
                                              as={RouterLink}
                                              to="/communications"
                                              className={`nav-item-custom ${isActive('/communications') ? 'active' : ''}`}
                                            >
                                                <FontAwesomeIcon icon={faEnvelope} className="nav-icon" />
                                                <span>Communications</span>
                                            </Nav.Link>
                                            <Nav.Link
                                              as={RouterLink}
                                              to="/attendance"
                                              className={`nav-item-custom ${isActive('/attendance') ? 'active' : ''}`}
                                            >
                                                <FontAwesomeIcon icon={faClipboardList} className="nav-icon" />
                                                <span>Attendance</span>
                                            </Nav.Link>
                                            <NavDropdown
                                              title={
                                                <span className={isActive('/admin/users') || isActive('/admin/settings') || isActive('/admin/activity') || isActive('/import-directory') ? 'admin-dropdown-active' : ''}>
                                                  <FontAwesomeIcon icon={faCog} className="nav-icon" />
                                                  <span>Settings</span>
                                                </span>
                                              }
                                              id="admin-dropdown"
                                              className={`nav-dropdown-custom ${isActive('/admin/users') || isActive('/admin/settings') || isActive('/admin/activity') || isActive('/import-directory') ? 'active' : ''}`}
                                            >
                                                <NavDropdown.Item as={RouterLink} to="/admin/users">
                                                    <FontAwesomeIcon icon={faUsers} className="dropdown-icon" />
                                                    Users
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={RouterLink} to="/admin/settings">
                                                    <FontAwesomeIcon icon={faBuilding} className="dropdown-icon" />
                                                    Organization Settings
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={RouterLink} to="/import-directory">
                                                    <FontAwesomeIcon icon={faFileImport} className="dropdown-icon" />
                                                    Import Directory
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={RouterLink} to="/admin/activity">
                                                    <FontAwesomeIcon icon={faHistory} className="dropdown-icon" />
                                                    Activity Log
                                                </NavDropdown.Item>
                                            </NavDropdown>
                                        </>
                                    )}
                                    {isSuperAdmin && (
                                        <NavDropdown
                                          title={
                                            <span className={isActive('/admin/data-audit') || isActive('/admin/org') || isActive('/admin/analytics') ? 'admin-dropdown-active' : ''}>
                                              <FontAwesomeIcon icon={faCog} className="nav-icon" />
                                              <span>Super Admin</span>
                                            </span>
                                          }
                                          id="super-admin-dropdown"
                                          className={`nav-dropdown-custom ${isActive('/admin/data-audit') || isActive('/admin/org') || isActive('/admin/analytics') ? 'active' : ''}`}
                                        >
                                            <NavDropdown.Item as={RouterLink} to="/admin/data-audit">
                                                <FontAwesomeIcon icon={faClipboardList} className="dropdown-icon" />
                                                Data Audit
                                            </NavDropdown.Item>
                                            <NavDropdown.Item as={RouterLink} to="/admin/org">
                                                <FontAwesomeIcon icon={faBuilding} className="dropdown-icon" />
                                                Organization
                                            </NavDropdown.Item>
                                            <NavDropdown.Item as={RouterLink} to="/admin/analytics">
                                                <FontAwesomeIcon icon={faChartLine} className="dropdown-icon" />
                                                Analytics
                                            </NavDropdown.Item>
                                        </NavDropdown>
                                    )}
                                </>
                            )}
                        </Nav>
                        {/* Right Nav Items */}
                        <Nav className="align-items-lg-center">
                            {currentUser ? (
                            <>
                                <Nav.Link
                                  as={RouterLink}
                                  to="/guides"
                                  title="Guides"
                                  className={`nav-item-custom nav-item-icon-only d-none d-lg-flex ${isActive('/guides') ? 'active' : ''}`}
                                >
                                    <FontAwesomeIcon icon={faQuestionCircle} />
                                </Nav.Link>
                                {/* Mobile: show guides as full item */}
                                <Nav.Link
                                  as={RouterLink}
                                  to="/guides"
                                  className={`nav-item-custom d-lg-none ${isActive('/guides') ? 'active' : ''}`}
                                >
                                    <FontAwesomeIcon icon={faQuestionCircle} className="nav-icon" />
                                    <span>Guides</span>
                                </Nav.Link>
                                {/* Desktop: User dropdown */}
                                <NavDropdown
                                  title={
                                    <span className="user-dropdown-toggle">
                                      {memberPhoto ? (
                                        <MemberPhotoAvatar src={memberPhoto} alt="Profile" size={28} />
                                      ) : (
                                        <FontAwesomeIcon icon={faUserCircle} size="lg" />
                                      )}
                                    </span>
                                  }
                                  id="user-dropdown"
                                  align="end"
                                  className="user-dropdown d-none d-lg-block"
                                >
                                    <div className="dropdown-user-info">
                                        <small className="text-muted">Signed in as</small>
                                        <div className="user-email">{currentUser.email}</div>
                                    </div>
                                    <NavDropdown.Divider />
                                    <NavDropdown.Item as={RouterLink} to="/profile">
                                        <FontAwesomeIcon icon={faUserCircle} className="dropdown-icon" />
                                        My Profile
                                    </NavDropdown.Item>
                                    <NavDropdown.Divider />
                                    <NavDropdown.Item onClick={() => auth.signOut()} className="logout-item">
                                        <FontAwesomeIcon icon={faSignOutAlt} className="dropdown-icon" />
                                        Sign Out
                                    </NavDropdown.Item>
                                </NavDropdown>
                                {/* Mobile: Profile and Logout as separate items */}
                                <Nav.Link
                                  as={RouterLink}
                                  to="/profile"
                                  className={`nav-item-custom d-lg-none ${isActive('/profile') ? 'active' : ''}`}
                                >
                                    {memberPhoto ? (
                                      <MemberPhotoAvatar src={memberPhoto} alt="Profile" size={20} />
                                    ) : (
                                      <FontAwesomeIcon icon={faUserCircle} className="nav-icon" />
                                    )}
                                    <span>My Profile</span>
                                </Nav.Link>
                                <Nav.Link
                                  onClick={() => auth.signOut()}
                                  className="nav-item-custom nav-logout d-lg-none"
                                >
                                    <FontAwesomeIcon icon={faSignOutAlt} className="nav-icon" />
                                    <span>Sign Out</span>
                                </Nav.Link>
                            </>
                            ) : (
                                isMarketingPage && (
                                    <>
                                        <Nav.Link
                                          as={RouterLink}
                                          to="/features"
                                          className={`nav-item-custom ${isActive('/features') ? 'active' : ''}`}
                                        >
                                            <span>Features</span>
                                        </Nav.Link>
                                        <Nav.Link
                                          as={RouterLink}
                                          to="/features#pricing"
                                          className={`nav-item-custom ${location.hash === '#pricing' ? 'active' : ''}`}
                                        >
                                            <span>Pricing</span>
                                        </Nav.Link>
                                        <Nav.Link
                                          as={RouterLink}
                                          to="/guides"
                                          className={`nav-item-custom ${isActive('/guides') ? 'active' : ''}`}
                                        >
                                            <span>Guides</span>
                                        </Nav.Link>
                                        <Nav.Link
                                          as={RouterLink}
                                          to="/register-organization"
                                          className="nav-cta-btn"
                                        >
                                            Register Your Church
                                        </Nav.Link>
                                    </>
                                )
                            )}
                        </Nav>
                    </Navbar.Collapse>
                </Container>
            </Navbar>
        </header>

        {/* Main Content Area - Routing (Unchanged) */}
        <main role="main" className="container-fluid mt-3 flex-grow-1">
          <Routes>
            {/* ... (routes remain the same) ... */}
             {/* --- Public Routes --- */}
             <Route path="/features" element={<FeaturesPage />} />
             <Route path="/guides" element={<GuidesIndex />} />
             <Route path="/guides/:guideSlug" element={<GuidePage />} />
             <Route path="/join/:tokenId" element={<JoinPage />} />
             <Route path="/newsletter/:orgSlug" element={<NewsletterSignupPage />} />
             <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/create-account" element={<CreateAccountPage />} />
            <Route path="/create-account/:orgId" element={<CreateAccountPage />} />
            <Route path="/register-organization" element={<RegisterOrganizationPage />} />
            <Route path="/" element={currentUser ? <Navigate to="/dashboard" replace /> : <Home />} />
            <Route path="/login" element={!currentUser ? <Login /> : <Navigate to="/dashboard" replace />} />
            <Route path="/forgot-password" element={!currentUser ? <ForgotPassword /> : <Navigate to="/dashboard" replace />} />

            {/* --- Protected Routes --- */}
            <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
            <Route path="/map" element={<ProtectedRoute element={<MapPage />} />} />

            {/* --- Families Routes (Corrected Order & Syntax) --- */}
            <Route path="/families" element={<ProtectedRoute element={<FamilyList />} />} />
            <Route path="/families/add" element={<ProtectedRoute element={<AddFamilyPage />} requireAdmin />} />
            <Route path="/import-directory" element={<ProtectedRoute element={<ImportDirectoryPage />} requireAdmin />} />
            <Route path="/families/edit/:familyId" element={<ProtectedRoute element={<EditFamilyPage />} />} />
            <Route path="/families/:familyId/assign-photos/all" element={<ProtectedRoute element={<AssignMemberPhotos />} />} />
            <Route path="/families/:familyId/assign-photos" element={<ProtectedRoute element={<AssignSingleMemberPhotoPage />} />} />
            <Route path="/families/:familyId/member/:memberId" element={<ProtectedRoute element={<MemberDetail />} />} />
            <Route path="/families/:familyId" element={<ProtectedRoute element={<FamilyDetail />} />} />

            {/* --- Other Routes --- */}
            <Route path="/groups" element={<ProtectedRoute element={<GroupsPage />} />} />
            <Route path="/groups/add" element={<ProtectedRoute element={<AddGroupPage />} requireAdmin />} />
            <Route path="/events" element={<ProtectedRoute element={<EventsPage />} />} />
            <Route path="/events/add" element={<ProtectedRoute element={<AddEventPage />} requireAdmin />} />
            <Route path="/events/:eventId/edit" element={<ProtectedRoute element={<EditEventPage />} requireAdmin />} />
            <Route path="/events/:eventId" element={<ProtectedRoute element={<EventDetailPage />} />} />
            <Route path="/members/add" element={<ProtectedRoute element={<AddMemberPage />} />} />
            <Route path="/members/edit/:memberId" element={<ProtectedRoute element={<EditMemberPage />} />} />
            <Route path="/admin/users" element={<ProtectedRoute element={<UserManagement />} requireAdmin />} />
            <Route path="/admin/join-requests" element={<ProtectedRoute element={<JoinRequestsPage />} requireAdmin />} />
            <Route path="/admin/activity" element={<ProtectedRoute element={<ActivityLogPage />} requireAdmin />} />
            <Route path="/communications" element={<ProtectedRoute element={<CommunicationsPage />} requireAdmin />} />
            <Route path="/communications/new" element={<ProtectedRoute element={<AnnouncementForm />} requireAdmin />} />
            <Route path="/communications/:id/edit" element={<ProtectedRoute element={<AnnouncementForm />} requireAdmin />} />
            <Route path="/communications/automations/new" element={<ProtectedRoute element={<AutomationForm />} requireAdmin />} />
            <Route path="/communications/automations/:id/edit" element={<ProtectedRoute element={<AutomationForm />} requireAdmin />} />
            <Route path="/admin/settings" element={<ProtectedRoute element={<OrganizationSettingsPage />} requireAdmin />} />
            <Route path="/admin/data-audit" element={<ProtectedRoute element={<DataAuditPage />} requireAdmin />} />
            <Route path="/admin/org" element={<ProtectedRoute element={<OrgManagementPage />} requireAdmin />} />
            <Route path="/admin/analytics" element={<ProtectedRoute element={<AnalyticsDashboardPage />} requireAdmin />} />
            <Route path="/attendance" element={<ProtectedRoute element={<AttendancePage />} requireAdmin />} />
            <Route path="/attendance/types" element={<ProtectedRoute element={<AttendanceTypesPage />} requireAdmin />} />

            {/* --- Profile Route (Login required, verification not) --- */}
            <Route
                path="/profile"
                element={currentUser ? <UserProfilePage /> : <Navigate to="/" replace />}
            />

            {/* --- Fallback Routes --- */}
            {currentUser && <Route path="*" element={<Navigate to="/dashboard" replace />} />}
            {!currentUser && <Route path="*" element={ <div className='text-center mt-5'> <h2>404 Not Found</h2> <p>The page you requested could not be found.</p> <RouterLink to="/">Go to Home</RouterLink> </div> } />}
          </Routes>
        </main>

        <Footer />
      </div>
  );
}

// Main App component (Unchanged)
function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;